// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import BackdropProgress from '../../BackdropProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClearIcon from "@material-ui/icons/Clear";
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import CustomSnackbar from '../ui/Snackbars';
import Dialog from '@mui/material/Dialog';
import EditIcon from '@mui/icons-material/Edit';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import useHttp from '../../utils/http';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { entryTypes, travelDocumentTypes, visaStatus, visaStatusArray, visaTypes } from '../../utils/const';
import { postSearchTravelDocuments, patchEmployeeVisa, postVisaImage } from '../../utils/end-points.js';
import { FileUploader } from "react-drag-drop-files";
import { adjustForTimezone, getCountryList, getCountryNameFromCountryCode, getDateForDB } from '../../utils/formatting';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { makeStyles } from '@mui/styles';
import { useState, useEffect } from 'react';
import { getVisaStatusColor } from '../../theme/status-colors';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import "../../css/EditVisaDialog.scss"

const useStyles = makeStyles(() => ({
    paper: { minWidth: "700px" },
}));

function EditVisaDialog(props) {

    const classes = useStyles();
    const { open, onClose, visa, image, refreshVisaList } = props
    const { handleRequest, handleFileRequest } = useHttp();
    const [passportInputValue, setPassportInputValue] = useState('')
    const [passportList, setPassportList] = useState([])
    const [country, setCountry] = useState(null)
    const [visaType, setVisaType] = useState(visa.visaType)
    const [entryType, setEntryType] = useState(visa.entryType)
    const [validFrom, setValidFrom] = useState(adjustForTimezone(new Date(visa.startDate)))
    const [validTo, setValidTo] = useState(adjustForTimezone(new Date(visa.endDate)))
    const [issuedDate, setIssuedDate] = useState(adjustForTimezone(new Date(visa.issuedDate)))
    const [status, setStatus] = useState(visa.status)
    const [visaImage, setVisaImage] = useState(image)
    const [isVisaImageChanged, setVisaImageChanged] = useState(false)
    const [isVisaImageEditable, setVisaImageEditable] = useState(false)
    const [passport, setPassport] = useState(null)
    const [isFormError, setFormError] = useState(false)
    const [isDatesError, setDateError] = useState(false)
    const [isVisaIsAllocated, setVisaAllocated] = useState(false)
    const [isPassportListLoading, setPassportListLoading] = useState(true)
    const [isPageLoading, setPageLoading] = useState(false)
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })
    const [confirmationDialogData, setConfirmationDialogData] = useState({ open: false, title: "", content: "", acceptLabel: "", declineLabel: "", acceptAlert: null, declineAlert: null })

    useEffect(() => {
        fetchData()

        var selectedCountry = getCountryList().filter(country => country.countryCode === visa.country)
        if (selectedCountry.length > 0) {
            setCountry(selectedCountry[0])
        }
    }, [])

    const fetchData = () => {
        let body = {
            status: "active",
            offset: 0,
            email: visa.email,
            documentType: travelDocumentTypes.PASSPORT,
        }
        handleRequest(postSearchTravelDocuments, "POST", body, (passports) => {
            if (passports) {
                let selectedPassport = passports.filter(passport => passport.id === visa.passportId)
                if (selectedPassport.length > 0) {
                    setPassport(selectedPassport[0])
                }
                var currentDate = new Date()
                let passportOptions = []
                passports = passports.filter(document => document.documentType === travelDocumentTypes.PASSPORT)
                passports.map(passport => {
                    var passportEndDate = adjustForTimezone(new Date(passport.expiryDate))
                    if (currentDate <= passportEndDate) {
                        passport['isExpired'] = false
                        passportOptions.push(passport)
                    }
                })
                passports.map(passport => {
                    var passportEndDate = adjustForTimezone(new Date(passport.expiryDate))
                    if (currentDate > passportEndDate) {
                        passport['isExpired'] = true
                        passportOptions.push(passport)
                    }
                })
                setPassportList(passportOptions)

            }
            else if (!passports) {
                setSnackbarData({ message: "Error while fetching Travel Documents", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setPassportListLoading(false)
        }, (error) => {
            setPassportListLoading(false)
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })

    }

    const handleSubmit = () => {

        if (status !== visaStatus.INACTIVE && (!country || !visaType || !entryType || !validFrom || !validTo || !issuedDate || !passport || !visaImage || !status)) {
            setFormError(true)
            setSnackbarData({ message: "Please check the form again", open: true, severity: "error", onClose: onCloseSnackbar })
        }
        else {
            setFormError(false)
            if (validFrom > validTo) {
                setSnackbarData({ message: "Please check the dates again", open: true, severity: "error", onClose: onCloseSnackbar })
                setDateError(true)
            }
            else {

                var contentMsg = "Please make sure the information are correct. Are you sure you want to edit this Visa?"
                if (status === status.INACTIVE) {
                    contentMsg = "You are about to Inactivate this Visa. This can not be undone. Please make sure the information are correct. Are you sure you want to edit this Visa?"
                }
                setConfirmationDialogData({
                    open: true,
                    title: "Edit Visa",
                    content: contentMsg,
                    acceptLabel: "Yes",
                    declineLabel: "No",
                    acceptAlert: saveVisa,
                    declineAlert: handleDeclineAlert
                })
            }
        }

    }

    const saveVisa = () => {

        setConfirmationDialogData(data => ({ ...data, open: false }))
        setPageLoading(true)
        setDateError(false)

        const body = {
            id: visa.id,
            email: visa.email,
            country: country.countryCode,
            visaType: visaType,
            startDate: getDateForDB(new Date(validFrom)),
            endDate: getDateForDB(new Date(validTo)),
            entryType: entryType,
            issuedDate: getDateForDB(new Date(issuedDate)),
            passportId: passport?.id,
            status: status
        }

        handleRequest(patchEmployeeVisa, "PATCH", body, (response) => {
            if (response.id > 0) {
                if (isVisaImageChanged) {

                    let fileExtension = visaImage.name.substring(visaImage.name.lastIndexOf('.') + 1, visaImage.name.length)
                    let fileName = country.countryCode + "." + fileExtension;

                    const reader = new FileReader()
                    reader.readAsDataURL(visaImage);
                    reader.onload = () => {
                        const encodedFile = btoa(reader.result)
                        const fileBody = {
                            encodedFile: encodedFile
                        }
                        handleRequest(postVisaImage.replace("$visaId", response.id).replace("$imageName", fileName), "POST", fileBody, (visa) => {
                            if (visa.id > 0) {
                                handleClose()
                                onClose()
                                refreshVisaList()
                            }
                            else {
                                setSnackbarData({ message: "Visa image upload failed!", open: true, severity: "error", onClose: onCloseSnackbar })
                                setPageLoading(false)
                            }
                        }, (error) => {
                            console.error(error)
                            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                            setPageLoading(false)
                        })
                    };

                }
                else {
                    onClose()
                    refreshVisaList()
                    setPageLoading(false)
                }
            }
            else {
                setPageLoading(false)
                setSnackbarData({ message: "Visa image upload failed!", open: true, severity: "error", onClose: onCloseSnackbar })
                setPageLoading(false)
            }
        }, (error) => {
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
            setPageLoading(false)
        })
    }

    const handleVisaImage = (file) => {
        setVisaImage(file)
        setVisaImageChanged(true)
    }

    const fileUploadSizeError = () => {
        console.error("Size cannot exceed 1MB")
        setSnackbarData({ message: "Size cannot exceed 1MB", open: true, severity: "error", onClose: onCloseSnackbar })
        setVisaImage(null)
    }

    const handleClose = () => {
        onClose()
        setCountry(null)
        setVisaType(null)
        setEntryType(null)
        setValidFrom(null)
        setValidTo(null)
        setIssuedDate(null)
        setVisaImage(null)
        setPassport(null)
        setFormError(false)
        onCloseSnackbar()
    }

    const setPassportValue = (value) => {
        setPassport(value)
    }


    const handleDeclineAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (
        <Dialog onClose={() => handleClose()} open={open} classes={{ paper: classes.paper }} className="Edit-visa-dialog">

            <CustomSnackbar snackbar={snackbarData} />

            <ConfirmationDialog confirmationDialogData={confirmationDialogData} />

            <BackdropProgress open={isPassportListLoading || isPageLoading} />

            <div className='Dialog-div'>

                <div className='Dialog-title'>Edit Visa</div>

                <div>
                    <div className='Fields-required'><span className='Required-span'>*</span> All fields are required</div>
                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Email</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            <TextField disabled fullWidth size="small" label={visa.email} />
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Country</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            <Autocomplete
                                size="small"
                                value={country}
                                disabled={isVisaIsAllocated}
                                disablePortal
                                isOptionEqualToValue={(option, value) => option.country === value.country}
                                fullWidth
                                onChange={(event, value) => setCountry(value)}
                                options={getCountryList()}
                                getOptionLabel={(option) => option.country}
                                renderOption={(props, option) => (
                                    <Box {...props}>{option.country}</Box>
                                )}
                                renderInput={(params) => <TextField  {...params} label="Country" error={isFormError && !country} />}
                            />
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Visa Type</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            <Autocomplete
                                disabled={isVisaIsAllocated}
                                size="small"
                                noOptionsText='No options'
                                disablePortal
                                value={visaType}
                                fullWidth
                                onChange={(event, value) => setVisaType(value)}
                                options={visaTypes}
                                getOptionLabel={(option) => option}
                                renderOption={(props, option) => (
                                    <Box {...props}>{option}</Box>
                                )}
                                renderInput={(params) => <TextField  {...params} label="Visa Type" error={isFormError && !visaType} />}
                            />
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Entry Type</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            <Autocomplete
                                disabled={isVisaIsAllocated}
                                size="small"
                                noOptionsText='No options'
                                disablePortal
                                value={entryType}
                                fullWidth
                                onChange={(event, value) => setEntryType(value)}
                                options={entryTypes}
                                getOptionLabel={(option) => option}
                                renderOption={(props, option) => (
                                    <Box {...props}>{option}</Box>
                                )}
                                renderInput={(params) => <TextField  {...params} label="Entry Type" error={isFormError && !entryType} />}
                            />
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Valid From</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    disabled={isVisaIsAllocated}
                                    label="Valid From"
                                    inputFormat='yyyy-MM-dd'
                                    mask='____-__-__'
                                    value={validFrom}
                                    onChange={(value) => setValidFrom(value)}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => setValidFrom(null)} size="small" disabled={isVisaIsAllocated}>
                                                <ClearIcon />
                                            </IconButton>
                                        )
                                    }}
                                    InputAdornmentProps={{
                                        position: "start"
                                    }}
                                    renderInput={(params) => <TextField size="small" {...params} fullWidth error={(isFormError && !validFrom) || isDatesError} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Valid To</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    disabled={isVisaIsAllocated}
                                    label="Valid To"
                                    inputFormat='yyyy-MM-dd'
                                    mask='____-__-__'
                                    value={validTo}
                                    onChange={(value) => setValidTo(value)}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => setValidTo(null)} size="small" disabled={isVisaIsAllocated}>
                                                <ClearIcon />
                                            </IconButton>
                                        )
                                    }}
                                    InputAdornmentProps={{
                                        position: "start"
                                    }}
                                    renderInput={(params) => <TextField size="small" {...params} fullWidth error={(isFormError && !validTo) || isDatesError} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Issued Date</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    disabled={isVisaIsAllocated}
                                    label="Issued Date"
                                    inputFormat='yyyy-MM-dd'
                                    mask='____-__-__'
                                    value={issuedDate}
                                    onChange={(value) => setIssuedDate(value)}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => setIssuedDate(null)} size="small" disabled={isVisaIsAllocated}>
                                                <ClearIcon />
                                            </IconButton>
                                        )
                                    }}
                                    InputAdornmentProps={{
                                        position: "start"
                                    }}
                                    renderInput={(params) => <TextField size="small" {...params} fullWidth error={isFormError && !issuedDate} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Passport</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            <Autocomplete
                                size="small"
                                noOptionsText='No active passports'
                                loading={isPassportListLoading}
                                disablePortal
                                disabled={isVisaIsAllocated}
                                fullWidth
                                value={passport}
                                onChange={(_, newValue) => {
                                    setPassportValue(newValue)
                                }}
                                inputValue={passportInputValue}
                                onInputChange={(_, newInputValue) => {
                                    setPassportInputValue(newInputValue)
                                }}
                                options={passportList.filter(passport => visa.email && (passport.email === visa.email))}
                                getOptionDisabled={(option) => option.isExpired === true}
                                getOptionLabel={(option) => getCountryNameFromCountryCode(option.issuedCountry) + " - " + option.documentNo + " ( " + getDateForDB(adjustForTimezone(new Date(option.issuedDate))) + " - " + getDateForDB(adjustForTimezone(new Date(option.expiryDate))) + " )"}
                                renderOption={(props, option) => (
                                    <Box style={{ fontSize: 15, color: option.isExpired ? "red" : null }}  {...props}>{getCountryNameFromCountryCode(option.issuedCountry) + " - " + option.documentNo + " ( " + getDateForDB(adjustForTimezone(new Date(option.issuedDate))) + " - " + getDateForDB(adjustForTimezone(new Date(option.expiryDate))) + " )"}</Box>
                                )}
                                renderInput={(params) => <TextField {...params} label="Passport" error={isFormError && !passport} />}
                            />
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Status</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            <FormControl fullWidth size="small" disabled={isVisaIsAllocated}>
                                <InputLabel>Status</InputLabel>
                                <Select value={status} label="Status" onChange={(event) => setStatus(event.target.value)}>
                                    {visaStatusArray.length > 0 && visaStatusArray.filter(status => status !== visaStatus.EXPIRED).map(status => {
                                        return <MenuItem key={status} value={status}>
                                            <span className="Status-dot-dropdown" style={{ backgroundColor: (status == visaStatus.ACTIVE) ? getVisaStatusColor(true) : getVisaStatusColor(false) }}></span>{status}
                                        </MenuItem>
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Visa Image</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={9} className='Grid-item'>
                            {(isVisaImageEditable || !image) ? (<div className={(isFormError && !visaImage) ? "File-upload-div-error" : "File-upload-div"}>
                                <FileUploader handleChange={file => (handleVisaImage(file))} maxSize={1} onSizeError={() => fileUploadSizeError()}
                                    name="file" label="Browse or Drag and Drop Image. Please upload an image of the visa information page (Max Size: 1Mb)." types={["JPG", "PNG", "JPEG"]} />
                            </div>) : (
                                <div className='Grid-item'>
                                    <img id="image" src={image.src} alt={image.name} className='Visa-image' />
                                    <Button disabled={isVisaIsAllocated} size="small" variant="outlined" startIcon={<EditIcon />} color="primary" className='Edit-image-button' onClick={() => { setVisaImageEditable(true) }} >Edit Image</Button>
                                </div>
                            )}

                        </Grid>
                    </Grid>
                    {isVisaIsAllocated && <div className='Visa-allocated-label'>Visa is already allocated. You can not edit this Visa.</div>}
                    <div className='Button-grid'>
                        {!isVisaIsAllocated && <Button variant="contained" color="secondary" className="Submit-button" onClick={() => handleSubmit()}>Submit</Button>}
                        <Button variant="outlined" color="primary" className="Reset-button" onClick={() => handleClose()}>Cancel</Button>
                    </div>
                </div>

            </div>
        </Dialog >
    );
}

export default EditVisaDialog;
